import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Zap, Crown, Loader2, Sparkles } from "lucide-react";

const fmt = (cents) => (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const Pricing = ({ onRequireAuth }) => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [freePlan, setFreePlan] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => { setPlans(data.plans); setFreePlan(data.free_plan || null); }).catch(() => {});
  }, []);

  const checkout = async (plan) => {
    if (!user) { onRequireAuth(); return; }
    const lookup_key = (yearly ? plan.yearly : plan.monthly).lookup_key;
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  const meta = {
    pro: { Icon: Zap, accent: "text-brand", ring: "border-white/10", glow: "" },
    elite: { Icon: Crown, accent: "text-amber", ring: "border-brand/50", glow: "glow-blue" },
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-20 lg:py-28 border-b border-white/5 bg-[#0A0D13]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="font-data text-xs uppercase tracking-[0.2em] text-brand">◆ Membership</span>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Trade on a real edge</h2>
          <p className="mt-3 text-slate-400 text-base leading-relaxed">
            Unlock the Netroy Chart Analyzer at scale. Cancel anytime. Save 15% with yearly billing.
          </p>

          <div className="mt-7 inline-flex items-center gap-3 p-1 rounded-full border border-white/10 bg-[#0C1017]" data-testid="billing-cycle-toggle">
            <button
              data-testid="billing-monthly-btn"
              onClick={() => setYearly(false)}
              className={`px-5 py-2 rounded-full font-data text-xs uppercase tracking-wider transition-colors ${!yearly ? "bg-brand text-white" : "text-slate-400 hover:text-white"}`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-yearly-btn"
              onClick={() => setYearly(true)}
              className={`px-5 py-2 rounded-full font-data text-xs uppercase tracking-wider transition-colors flex items-center gap-2 ${yearly ? "bg-brand text-white" : "text-slate-400 hover:text-white"}`}
            >
              Yearly <span className="font-data text-[10px] px-1.5 py-0.5 rounded bg-gain/15 text-gain border border-gain/30">-15%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
          {freePlan && (
            <motion.div
              data-testid="pricing-card-free"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative card-surface rounded-2xl p-7"
            >
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-lg border border-white/10 bg-white/5 grid place-items-center">
                  <Sparkles className="h-6 w-6 text-slate-300" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-extrabold tracking-tight">{freePlan.name}</h3>
                  <p className="font-data text-[11px] text-slate-500">{freePlan.tagline}</p>
                </div>
              </div>
              <div className="mt-6 flex items-end gap-2">
                <span className="font-heading text-5xl font-black tracking-tight">$0.00</span>
                <span className="font-data text-sm text-slate-500 mb-1.5">/ month</span>
              </div>
              <p className="font-data text-[11px] text-slate-500 mt-1">Free forever · no card required</p>
              <ul className="mt-6 space-y-3">
                {freePlan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-subscribe-free-btn"
                onClick={() => { if (!user) onRequireAuth(); }}
                disabled={user?.plan === "free"}
                className="w-full mt-7 h-12 font-semibold bg-white/8 hover:bg-white/15 text-white border border-white/10"
              >
                {user?.plan === "free" ? "Current Plan" : user ? "Included" : "Get Started Free"}
              </Button>
            </motion.div>
          )}
          {plans.map((plan, i) => {
            const m = meta[plan.id] || meta.pro;
            const cents = yearly ? plan.yearly.amount : plan.monthly.amount;
            const perMonth = yearly ? Math.round(plan.yearly.amount / 12) : plan.monthly.amount;
            const isCurrent = user?.plan === plan.id;
            const featured = plan.id === "elite";
            const lookup_key = (yearly ? plan.yearly : plan.monthly).lookup_key;
            return (
              <motion.div
                key={plan.id}
                data-testid={`pricing-card-${plan.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`relative card-surface rounded-2xl p-7 ${featured ? `${m.ring} ${m.glow}` : ""}`}
              >
                {featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 font-data text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-brand text-white">
                    <Sparkles className="h-3 w-3" /> Most Popular
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg border border-white/10 bg-white/5 grid place-items-center">
                    <m.Icon className={`h-6 w-6 ${m.accent}`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-extrabold tracking-tight">{plan.name}</h3>
                    <p className="font-data text-[11px] text-slate-500">{plan.tagline}</p>
                  </div>
                </div>

                <div className="mt-6 flex items-end gap-2">
                  <span className="font-heading text-5xl font-black tracking-tight">${fmt(perMonth)}</span>
                  <span className="font-data text-sm text-slate-500 mb-1.5">/ month</span>
                </div>
                <p className="font-data text-[11px] text-slate-500 mt-1">
                  {yearly ? `Billed $${fmt(cents)} yearly` : "Billed monthly"}
                  {yearly && <span className="text-gain"> · save 15%</span>}
                </p>

                <ul className="mt-6 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${m.accent}`} /> {f}
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`pricing-subscribe-${plan.id}-btn`}
                  onClick={() => checkout(plan)}
                  disabled={loadingKey === lookup_key || isCurrent}
                  className={`w-full mt-7 h-12 font-semibold gap-2 ${featured ? "bg-brand hover:bg-brand-hover text-white" : "bg-white/8 hover:bg-white/15 text-white border border-white/10"}`}
                >
                  {loadingKey === lookup_key ? <Loader2 className="h-4 w-4 animate-spin" />
                    : isCurrent ? "Current Plan"
                    : user ? `Upgrade to ${plan.name.replace("Netroy ", "")}` : "Sign in to Subscribe"}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center font-data text-[11px] text-slate-600 mt-8">
          Secure checkout by Stripe · Test card 4242 4242 4242 4242 · Cancel anytime
        </p>
      </div>
    </section>
  );
};
